/**
 * The stable machine vocabulary for failures. Clients branch on `code`, never on
 * `message` — a message is prose that may be reworded, a code is a contract.
 *
 * Adding a failure mode means adding a code here first.
 */
export const ERROR_CODES = {
  VALIDATION_FAILED: "VALIDATION_FAILED",
  UNAUTHENTICATED: "UNAUTHENTICATED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  CONFLICT: "CONFLICT",
  /** Optimistic-concurrency refusal. `details.current` carries the server's current state. */
  STALE_VERSION: "STALE_VERSION",
  RATE_LIMITED: "RATE_LIMITED",
  /** A dependency the request needed is down — distinct from a fault in our code. */
  UNAVAILABLE: "UNAVAILABLE",
  INTERNAL: "INTERNAL",
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

/**
 * The single error envelope, RFC 7807-shaped. Produced by one global filter in the
 * API; parsed by one client wrapper in the web app. Nothing else shapes an error.
 */
export type ApiError = {
  status: number;
  code: ErrorCode;
  /** Safe to show a user. Diagnostics belong in the log, keyed by `requestId`. */
  message: string;
  details?: unknown;
  /**
   * The user-facing diagnostic identifier. Echoed as `X-Request-Id`, present in
   * every log line for the request, and rendered by the client's error state so a
   * support conversation starts from a reference rather than a description.
   */
  requestId: string;
};

/** Field-level shape carried in `details` when `code` is `VALIDATION_FAILED`. */
export type ValidationDetails = {
  fieldErrors: Record<string, string[]>;
  formErrors: string[];
};
